import { useState } from 'react';
import { mensajeDe, normalizarTelefonoCO, TIPOS_DOCUMENTO_DIAN, validarDocumento } from '@tanteo/compartido';
import { Boton, Campo, CampoCelular, CampoLista, FilaAcciones, FilaCampos, MensajeError, TextoAyuda } from './base';
import { instalacion } from '@tanteo/compartido/instalacion';

/**
 * El único formulario del negocio y su titular: lo usan el registro y la reparación de quien entró
 * sin membresía (`inicial` trae los datos de un registro a medias). Solo persona natural, y la
 * autorización de tratamiento de datos nunca llega prellenada.
 */

export interface DatosNegocioNuevo {
  nombre: string;
  prefijo: string;
  nombres: string;
  apellidos: string;
  docTipo: string;
  docNumero: string;
  telefono: string;
  /** docs/08 §3: casilla NO premarcada, y el servidor la exige. */
  autorizaDatos: boolean;
}

const TIPOS_TITULAR = TIPOS_DOCUMENTO_DIAN.filter((t) => t.contextos.includes('titular_negocio'));

export function FormularioNegocio({
  onListo,
  ocupado,
  errorServidor,
  onVolver,
  textoBoton = 'Crear negocio',
  inicial,
}: {
  /** Se llama con los datos ya validados. Quien lo use decide si los guarda o los envía. */
  onListo: (datos: DatosNegocioNuevo) => void | Promise<void>;
  ocupado: boolean;
  errorServidor?: string;
  onVolver?: () => void;
  textoBoton?: string;
  inicial?: Partial<Omit<DatosNegocioNuevo, 'autorizaDatos'>>;
}) {
  const [nombreNegocio, setNombreNegocio] = useState(inicial?.nombre ?? '');
  const [docTipo, setDocTipo] = useState(inicial?.docTipo ?? '13');
  const [docNumero, setDocNumero] = useState(inicial?.docNumero ?? '');
  const [nombres, setNombres] = useState(inicial?.nombres ?? '');
  const [apellidos, setApellidos] = useState(inicial?.apellidos ?? '');
  const [telefono, setTelefono] = useState(inicial?.telefono ?? '');
  const [autoriza, setAutoriza] = useState(false);
  const [error, setError] = useState('');

  function enviar() {
    setError('');
    if (nombreNegocio.trim().length < 3) return setError('Escribe el nombre del negocio (mínimo 3 letras).');
    if (nombres.trim().length < 2 || apellidos.trim().length < 2) return setError('Escribe tus nombres y apellidos.');
    if (!validarDocumento(docTipo, docNumero)) return setError('El documento no es válido para el tipo elegido.');
    // El celular no es opcional: es el único canal para avisarle al dueño de algo de su negocio.
    if (normalizarTelefonoCO(telefono) === null) return setError(mensajeDe('TELEFONO_INVALIDO'));
    if (!autoriza) return setError('Para crear la cuenta hay que autorizar el tratamiento de datos.');

    void onListo({
      nombre: nombreNegocio.trim(),
      prefijo: 'CMP-',
      nombres: nombres.trim(),
      apellidos: apellidos.trim(),
      docTipo,
      docNumero: docNumero.trim(),
      telefono: telefono.trim(),
      autorizaDatos: true,
    });
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Campo
          etiqueta="Nombre del negocio"
          value={nombreNegocio}
          onChange={(e) => setNombreNegocio(e.target.value)}
          autoFocus
        />
        <TextoAyuda>El nombre que ven tus clientes (ej. Tienda La 70).</TextoAyuda>
        <FilaCampos>
          <CampoLista etiqueta="Tipo de documento" value={docTipo} onChange={(e) => setDocTipo(e.target.value)}>
            {TIPOS_TITULAR.map((t) => (
              <option key={t.codigo} value={t.codigo}>
                {t.nombre}
              </option>
            ))}
          </CampoLista>
          <Campo etiqueta="Número" value={docNumero} onChange={(e) => setDocNumero(e.target.value)} />
        </FilaCampos>
      </div>

      <div>
        <p className="text-muted mb-1 text-contenido font-semibold">Responsable (quién atiende)</p>
        <FilaCampos>
          <Campo etiqueta="Nombres" value={nombres} onChange={(e) => setNombres(e.target.value)} autoComplete="given-name" />
          <Campo
            etiqueta="Apellidos"
            value={apellidos}
            onChange={(e) => setApellidos(e.target.value)}
            autoComplete="family-name"
          />
        </FilaCampos>
        <TextoAyuda className="mt-1 mb-0 text-etiqueta">
          Tu nombre sale en los comprobantes que registres, como quien atendió.
        </TextoAyuda>
      </div>

      <CampoCelular value={telefono} onChange={(e) => setTelefono(e.target.value)} className="num" />

      {/*
        docs/08-legal-normativo.md §3 y §5: casilla NO premarcada, con el texto expreso. El servidor la
        exige (`TN_CONSENTIMIENTO_OBLIGATORIO`) y estampa `consentimiento_datos_en` en la membresía como
        prueba.
      */}
      <label className="border-border rounded-control flex gap-2 border p-3 text-contenido">
        <input
          type="checkbox"
          checked={autoriza}
          onChange={(e) => setAutoriza(e.target.checked)}
          data-e2e="autoriza-datos"
          className="mt-0.5"
        />
        <span>
          Autorizo a {instalacion.textos.responsableDatos} el tratamiento de mis datos personales (nombre, documento, celular y correo)
          para operar mi negocio en la plataforma. Puedo consultar, actualizar o suprimir mis datos y
          revocar esta autorización escribiendo {instalacion.textos.soporte}.
        </span>
      </label>

      <MensajeError e2e="negocio-error">{error || errorServidor}</MensajeError>
      <FilaAcciones>
        {onVolver && (
          <Boton variante="contorno" onClick={onVolver} disabled={ocupado}>
            Volver
          </Boton>
        )}
        <Boton onClick={enviar} disabled={ocupado} data-e2e="crear-negocio">
          {ocupado ? 'Creando…' : textoBoton}
        </Boton>
      </FilaAcciones>
    </div>
  );
}
